import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      // We always show the same success state regardless of whether the
      // email exists — that's the standard anti-enumeration pattern.
      setSubmitted(true);
    } catch (e2) {
      toast.error(
        e2?.response?.data?.detail ||
          (!e2?.response
            ? "Couldn't reach the server. Please try again."
            : "Something went wrong. Please try again.")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-brand-cream min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-10">
      <div className="max-w-md w-full bg-white rounded-3xl border border-brand-border-warm p-8 shadow-warm animate-fade-in-up">
        <Link
          to="/login"
          data-testid="forgot-back-to-login"
          rel="nofollow"
          className="inline-flex items-center gap-1.5 text-sm text-brand-ink/60 hover:text-brand-forest mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </Link>

        {submitted ? (
          <div data-testid="forgot-success" className="text-center">
            <span className="inline-flex w-14 h-14 rounded-full bg-brand-light-green text-brand-forest items-center justify-center mb-4">
              <CheckCircle2 className="w-7 h-7" />
            </span>
            <h1 className="font-heading text-2xl font-black text-brand-ink">Check your inbox</h1>
            <p className="text-sm text-brand-ink/70 mt-3 leading-relaxed">
              If an account exists for <span className="font-semibold text-brand-ink">{email}</span>, we just sent a password reset link. The link expires in 30 minutes.
            </p>
            <p className="text-xs text-brand-ink/50 mt-4 leading-relaxed">
              Didn't get it? Check your spam folder, or wait a minute and try again.
            </p>
            <button
              type="button"
              onClick={() => { setSubmitted(false); setEmail(""); }}
              data-testid="forgot-try-different"
              className="mt-6 text-sm text-brand-forest font-semibold hover:underline"
            >
              Use a different email
            </button>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-black text-brand-ink">Forgot your password?</h1>
            <p className="text-sm text-brand-ink/70 mt-2 leading-relaxed">
              Enter your email and we'll send you a link to reset it.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-3">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-ink/40" />
                <input
                  data-testid="forgot-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full pl-11 pr-4 py-3 bg-brand-cream border border-brand-border-warm rounded-full focus:outline-none focus:ring-2 focus:ring-brand-forest"
                />
              </div>
              <button
                type="submit"
                data-testid="forgot-submit"
                disabled={loading}
                className="w-full bg-brand-forest text-white font-semibold rounded-full px-5 py-3 hover:bg-brand-forest-dark transition-colors active:scale-[0.98] shadow-warm disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Send reset link
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
